/**
 * GrantItem (Foundry: rule-element/grant-item): pull another Entry into the Character. Foundry
 * creates the granted item once, when the granting item is created. Here the engine reports
 * every grant it wants (`sheet.grants`) and the Character reconciler (src/character/grants.ts)
 * materialises each as a Selection of a Choice keyed by the granting Entry and Rule Element
 * index, so grants persist, show their origin, and leave with their granter. Predicates are
 * re-evaluated on every derive, so `reevaluateOnUpdate` needs no special handling.
 *
 * `inMemoryOnly` grants (a Condition an Effect applies while it lasts) are created inside the
 * engine, never written to the Character.
 */
import { applyAlteration, parseAlterations, type ItemAlteration } from "../alterations";
import { prepareConditionSiblings, setEffectRollOptions } from "../conditions";
import { EngineItem } from "../items";
import { resolveInjectedProperties } from "../resolve";
import { sluggify, sluggifyCamel } from "../slug";
import type { EngineState } from "../state";
import { PHYSICAL_ITEM_TYPES } from "../values";
import type { RuleHandler, RuleInstance } from "./base";
import { isItemUuid } from "./choice-set";

export type DeleteAction = "cascade" | "detach" | "restrict";
const DELETE_ACTIONS = ["cascade", "detach", "restrict"] as const;

export interface GrantItemData {
  uuid: string;
  flag: string | null;
  reevaluateOnUpdate: boolean;
  inMemoryOnly: boolean;
  allowDuplicate: boolean;
  onDeleteActions: { granter: DeleteAction | null; grantee: DeleteAction | null };
  preselectChoices: Record<string, string | number>;
  nestUnderGranter: boolean | null;
  alterations: ItemAlteration[];
  track: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deleteAction(value: unknown, field: string, rule: RuleInstance): DeleteAction | null | undefined {
  if (value === undefined || value === null) return null;
  if ((DELETE_ACTIONS as readonly string[]).includes(String(value))) return value as DeleteAction;
  rule.fail(`onDeleteActions.${field}: "${String(value)}" is not cascade, detach or restrict`);
  return undefined;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  if (typeof s.uuid !== "string" || !s.uuid) return rule.fail("uuid: must be a non-empty string");
  const inMemoryOnly = s.inMemoryOnly === true;
  let reevaluateOnUpdate = s.reevaluateOnUpdate === true;
  let allowDuplicate = s.allowDuplicate === undefined ? true : s.allowDuplicate === true;
  if (inMemoryOnly) {
    reevaluateOnUpdate = true;
    allowDuplicate = true;
  } else {
    if (reevaluateOnUpdate) allowDuplicate = false;
    if ((PHYSICAL_ITEM_TYPES as readonly string[]).includes(rule.item.type)) return rule.fail("parent item must not be physical");
  }
  const track = s.track === true;
  const flag = typeof s.flag === "string" && s.flag ? sluggifyCamel(s.flag) : null;
  if (track && !flag) return rule.fail("must have explicit flag set if granted item is tracked");
  if (reevaluateOnUpdate && !inMemoryOnly && rule.predicate.length === 0) return rule.fail("reevaluateOnUpdate: must have non-empty predicate");
  const actions = isPlainObject(s.onDeleteActions) ? s.onDeleteActions : {};
  const granter = deleteAction(actions.granter, "granter", rule);
  const grantee = deleteAction(actions.grantee, "grantee", rule);
  if (granter === undefined || grantee === undefined) return;
  const preselectChoices: Record<string, string | number> = {};
  // Upstream data misspells the key on one Entry; honour it the way Foundry's data model would not, since the intent is plain.
  const rawPreselect = s.preselectChoices ?? s.preselectCoices;
  if (rawPreselect !== undefined && rawPreselect !== null) {
    if (!isPlainObject(rawPreselect)) return rule.fail("preselectChoices: must be an object");
    for (const [key, value] of Object.entries(rawPreselect)) {
      if (!(typeof value === "string" || typeof value === "number")) return rule.fail(`preselectChoices.${key}: must be a string or number`);
      preselectChoices[key] = value;
    }
  }
  const { alterations, error } = parseAlterations(s.alterations);
  if (error) return rule.fail(error);
  const data: GrantItemData = {
    uuid: s.uuid,
    flag,
    reevaluateOnUpdate,
    inMemoryOnly,
    allowDuplicate,
    onDeleteActions: { granter, grantee },
    preselectChoices,
    nestUnderGranter: typeof s.nestUnderGranter === "boolean" ? s.nestUnderGranter : null,
    alterations,
    track,
  };
  rule.data = data as unknown as Record<string, unknown>;
}

/** The granted UUID with `{item|...}` placeholders resolved, or null (recorded on the rule) when it cannot be. */
export function resolveGrantUuid(rule: RuleInstance, state: EngineState): string | null {
  const data = rule.data as unknown as GrantItemData;
  const uuid: unknown = resolveInjectedProperties(rule, state, data.uuid, { warn: false });
  if (isItemUuid(uuid)) return uuid;
  const text = String(uuid);
  rule.problems.push(text.includes("{") || text.includes("undefined") ? `uuid: "${data.uuid}" did not resolve (is the ChoiceSet it reads answered?)` : `uuid: "${text}" is not an Item UUID`);
  return null;
}

/** Foundry's default `flag`: the granted Entry's slug in camel case, numbered when the granter already used it. */
export function grantFlag(rule: RuleInstance, grantedSlug: string, taken: Set<string>): string {
  const data = rule.data as unknown as GrantItemData;
  if (data.flag) return data.flag;
  const base = sluggifyCamel(grantedSlug);
  const pattern = new RegExp(`^${base}\\d*$`);
  const nth = [...taken].filter((f) => pattern.test(f)).length;
  return nth > 0 ? `${base}${nth + 1}` : base;
}

/** Preselected answers with `{item|...}` placeholders resolved against the granter. */
export function resolvedPreselections(rule: RuleInstance, state: EngineState): Record<string, string | number> {
  const data = rule.data as unknown as GrantItemData;
  const out: Record<string, string | number> = {};
  for (const [flag, value] of Object.entries(data.preselectChoices)) {
    const resolved = typeof value === "string" ? resolveInjectedProperties(rule, state, value, { warn: false }) : value;
    if (typeof resolved === "string" && resolved.includes("undefined")) continue;
    out[flag] = resolved;
  }
  return out;
}

/**
 * Foundry's `#createInMemoryCondition`: add the Condition to the engine's items for this derive
 * only. Needs the Condition Entry; when it is not loaded, the UUID is reported in `needed`.
 */
function onApplyActiveEffects(rule: RuleInstance, state: EngineState): void {
  const data = rule.data as unknown as GrantItemData;
  if (!data.inMemoryOnly || rule.ignored || !rule.test(state)) return;
  const uuid = resolveGrantUuid(rule, state);
  if (!uuid) return rule.fail("an in-memory-only grant must be a condition");
  const entry = state.entries.get(uuid);
  if (!entry) {
    state.needed.add(uuid);
    rule.problems.push(`in-memory Condition ${uuid} is not loaded`);
    return;
  }
  if (entry.type !== "condition") return rule.fail("an in-memory-only grant must be a condition");
  if (state.items.some((i) => i.grantedBy === rule.item && i.uuid === uuid && i.role === "temporary")) return;
  const item = new EngineItem({ entry, grantedBy: rule.item });
  item.inMemory = true;
  for (const alteration of data.alterations) {
    const problem = applyAlteration(rule, state, item, alteration);
    if (problem) rule.problems.push(problem);
  }
  const badge = (item.system.badge as { value?: unknown } | undefined)?.value;
  if (typeof badge === "number") item.badge = { value: badge };
  // Foundry: an in-memory Condition yields to a stored one of the same kind with an equal or higher value.
  const stored = state.items.find((i) => i.type === "condition" && i.active && !i.inMemory && i.conditionKey === item.conditionKey);
  const storedValue = stored?.badge?.value ?? null;
  const value = item.badge?.value ?? null;
  if (stored && (storedValue === value || (storedValue !== null && value !== null && storedValue >= value))) return;
  state.items.push(item);
  prepareConditionSiblings(state);
  if (!item.active) return;
  setEffectRollOptions(state, item);
  state.addRules(item);
}

export const GrantItemHandler: RuleHandler = {
  key: "GrantItem",
  priority: (source) => (source.inMemoryOnly === true ? 99 : 100),
  parse,
  onApplyActiveEffects,
};
